import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import Button from "components/form/Button";
import { SettingsValue } from "types/payments";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useUserData } from "store/slices/authSlice";
import {
  onSetFinalDisclosureLoading,
  onSetShowDisclosure,
  updateCheckAmountState,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import { usePortalSettingsData } from "store/slices/dashboardSlice";
import {
  duplicatePaymentGet,
  resetCheckDuplicatePayment,
  resetDuplicateOrder,
  storePaymnetPlan,
  setIsPaymentProcessing,
} from "store/slices/paymentsSlice";
import {
  checkOverPaymentAmount,
  checkPhone,
  formatCurrency,
  getCountryShortName,
  validateEmail,
} from "utils/HelperFunctions";
import { PhoneInput } from "types/common";
import { getCountryCallingCode } from "react-phone-number-input";
import { CountryCode } from "types/UserProfile";
import { RootState } from "store/store";
import MobileInputNew from "components/form/MobileInputNew";

interface AmazonPayFormProps {
  isSchedulePayment?: boolean;
  isUpdatePlan?: boolean;
  isRetry?: boolean;
  isEdit?: boolean;
  isSecretLoading?: boolean;
  isRecurring?: boolean;
  isAgreement?: boolean;
  isProductLink?: boolean;
  hasFuture?: boolean;
  isDisabled?: boolean;
  amount: string | number;
  handleBack?: () => void;
  paymentPlan?: any | null;
  stripeAmazonPayURL: string;
  buttonText?: string;
  email?: string;
  setEmail?: (val: string) => void;
  handleClick?: () => void;
  isUpdateSubscription?: boolean;
  checkNegativeBalance?: boolean;
  accounts_id?: string | null;
  collectPhone?: boolean;
  phoneNumber?: PhoneInput;
  setPhoneNumber?: React.Dispatch<React.SetStateAction<PhoneInput>>;
  isUpdateSubscriptionPaymentMethod?: boolean;
  isDowngradePlan?: boolean;
  handleEmailBlur?: () => void;
  orderId?: number;
  onIntentFailure?: (orderId: number, error: any) => Promise<void>;
}
let globalAmazonStripeInstance: any = null;
let globalAmazonElements: any = null;

const AmazonPayForm = forwardRef(
  (
    {
      isSchedulePayment = false,
      isUpdatePlan = false,
      isRetry = false,
      isSecretLoading = false,
      isEdit = false,
      isRecurring = false,
      hasFuture = false,
      isProductLink = false,
      isAgreement = false,
      isDisabled = false,
      handleBack,
      amount,
      paymentPlan = null,
      stripeAmazonPayURL = "",
      buttonText = "",
      email = "",
      setEmail = () => {},
      handleClick = () => {},
      isUpdateSubscription = false,
      checkNegativeBalance = false,
      accounts_id = "",
      collectPhone = false,
      phoneNumber = { country_code: "", phone: "", postal_code: "" },
      isUpdateSubscriptionPaymentMethod = false,
      setPhoneNumber = () => {},
      isDowngradePlan = false,
      handleEmailBlur = () => {},
      orderId = 0,
      onIntentFailure = async () => {},
    }: AmazonPayFormProps,
    ref
  ) => {
    const stripe = useStripe();
    const elements = useElements();
    const dispatch = useAppDispatch();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const portalSettings = usePortalSettingsData();
    const userData = useUserData();
    const showFinalDisclosure = useShowFinalDisclosure();
    const { checkAmount } = useAppSelector((state: RootState) => state.common);
    const { conFee, isFeeLoading, checkedDuplicatePayment } = useAppSelector(
      (state: RootState) => state.payment
    );
    const { isChecking, isSubscriptionAllowed } = useAppSelector(
      (state: RootState) => state.subscription
    );

    const handlePayment = async () => {
      try {
        if (!stripe || !elements) {
          return;
        }

        if (paymentPlan && !isRetry) {
          await dispatch(storePaymnetPlan(paymentPlan));
        }
        let payload = {};
        if (userData?.data?.accountData?.country) {
          payload = {
            ...payload,
            country: userData?.data?.accountData?.country ?? "US",
          };
        }
        if (userData?.data?.accountData?.city) {
          payload = {
            ...payload,
            city: userData?.data?.accountData?.city ?? "",
          };
        }
        if (userData?.data?.accountData?.postal_code) {
          payload = {
            ...payload,
            postal_code: userData?.data?.accountData?.postal_code ?? "",
          };
        }
        if (userData?.data?.accountData?.state) {
          payload = {
            ...payload,
            state: userData?.data?.accountData?.state ?? "",
          };
        }
        if (userData?.data?.accountData?.address1) {
          payload = {
            ...payload,
            line1: userData?.data?.accountData?.address1 ?? "",
          };
        }
        if (userData?.data?.accountData?.address2) {
          payload = {
            ...payload,
            line2: userData?.data?.accountData?.address2 ?? "",
          };
        }

        let phoneValue = collectPhone ? { phone: phoneNumber?.phone } : {};

        // Reset payment processing after redirect is initiated
        dispatch(setIsPaymentProcessing(false));
        const activeStripe = showFinalDisclosure && globalAmazonStripeInstance ? globalAmazonStripeInstance : stripe;
        const activeElements = showFinalDisclosure && globalAmazonElements ? globalAmazonElements : elements;

        const { error } = await activeStripe.confirmPayment({
          elements: activeElements,
          confirmParams: {
            return_url: stripeAmazonPayURL,
            payment_method_data: {
              billing_details: {
                ...phoneValue,
                name: userData?.data?.accountData?.first_name,
                email: isProductLink
                  ? email
                  : userData?.data?.accountData?.email,
                address: {
                  ...payload,
                },
              },
            },
          },
        });

        setIsLoading(false);

        if (error) {
          await onIntentFailure(orderId, error);
          console.log(error);
        }
      } catch (error) {
        setIsLoading(false);
        console.log("error =====", error);
      }
    };

    const handleSetup = async () => {
      try {
        if (!stripe || !elements) {
          dispatch(setIsPaymentProcessing(false));
          return;
        }

        if (paymentPlan && !isRetry) {
          await dispatch(storePaymnetPlan(paymentPlan));
        }

        let payload = {};
        if (userData?.data?.accountData?.country) {
          payload = {
            ...payload,
            country: userData?.data?.accountData?.country ?? "US",
          };
        }
        if (userData?.data?.accountData?.city) {
          payload = {
            ...payload,
            city: userData?.data?.accountData?.city ?? "",
          };
        }
        if (userData?.data?.accountData?.postal_code) {
          payload = {
            ...payload,
            postal_code: userData?.data?.accountData?.postal_code ?? "",
          };
        }
        if (userData?.data?.accountData?.state) {
          payload = {
            ...payload,
            state: userData?.data?.accountData?.state ?? "",
          };
        }
        if (userData?.data?.accountData?.address1) {
          payload = {
            ...payload,
            line1: userData?.data?.accountData?.address1 ?? "",
          };
        }
        if (userData?.data?.accountData?.address2) {
          payload = {
            ...payload,
            line2: userData?.data?.accountData?.address2 ?? "",
          };
        }

        let phoneValue = collectPhone ? { phone: phoneNumber?.phone } : {};
        // Reset payment processing after redirect is initiated
        dispatch(setIsPaymentProcessing(false));
        const activeStripe = showFinalDisclosure && globalAmazonStripeInstance ? globalAmazonStripeInstance : stripe;
        const activeElements = showFinalDisclosure && globalAmazonElements ? globalAmazonElements : elements;

        const { error } = await activeStripe.confirmSetup({
          elements: activeElements,
          confirmParams: {
            return_url: stripeAmazonPayURL,
            payment_method_data: {
              billing_details: {
                ...phoneValue,
                name: userData?.data?.accountData?.first_name,
                email: isProductLink
                  ? email
                  : userData?.data?.accountData?.email,
                address: {
                  ...payload,
                },
              },
            },
          },
        });

        setIsLoading(false);

        if (error) {
          await onIntentFailure(orderId, error);
          console.log(error.message);
          setIsLoading(false);
        }
      } catch (error) {
        setIsLoading(false);
        console.log("error =====", error);
      }
    };

    const checkPastAmount = async () => {
      try {
        let payload = {
          accounts_id: userData?.data?.accountData?.id ?? accounts_id,
          payment_method: "AMZP",
          amount: amount,
        };
        let res: any = await dispatch(duplicatePaymentGet(payload)).unwrap();
        const { isDuplicateOrders, duplicateOrdersCount } = res?.body?.data;
        if (isDuplicateOrders && duplicateOrdersCount > 0) {
          return false;
        } else {
          return true;
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handleCheckDisclosureAndDuplicate = async () => {
      try {
        let moveAhead =
          !checkedDuplicatePayment && !isProductLink
            ? await checkPastAmount()
            : true;
        handleClick();
        if (!moveAhead) {
          setIsLoading(false);
          dispatch(onSetFinalDisclosureLoading(false));
          return false;
        } else {
          if (
            (portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
              portalSettings?.payment_agreement_opt ===
                SettingsValue.DEFAULT) &&
            !showFinalDisclosure
          ) {
            setIsLoading(false);
            dispatch(onSetFinalDisclosureLoading(false));
            dispatch(resetDuplicateOrder());
            globalAmazonStripeInstance = stripe;
            globalAmazonElements = elements;
            dispatch(onSetShowDisclosure(true));
          } else {
            dispatch(resetDuplicateOrder());
            dispatch(resetCheckDuplicatePayment());
            return true;
          }
        }
      } catch (error) {
        console.log("error =====", error);
      }
    };

    const handleSubmit = async () => {
      setIsLoading(true);
      dispatch(setIsPaymentProcessing(true));
      if (paymentPlan) {
        await dispatch(storePaymnetPlan(paymentPlan));
      }

      if (!isUpdateSubscriptionPaymentMethod) {
        let negativeBalance =
          !checkAmount && checkNegativeBalance
            ? await checkOverPaymentAmount({
                amount: Number(amount),
                balance: userData?.data?.accountData?.balance,
                overpayment_protection: portalSettings?.overpayment_protection,
                dispatch,
              })
            : true;
        if (!negativeBalance) {
          setIsLoading(false);
          dispatch(setIsPaymentProcessing(false));
          return;
        }
        let moveAhead = await handleCheckDisclosureAndDuplicate();
        if (!moveAhead) {
          setIsLoading(false);
          dispatch(setIsPaymentProcessing(false));
          return;
        }
        dispatch(updateCheckAmountState(false));
      }
      try {
        if (!stripe || !elements) {
          dispatch(setIsPaymentProcessing(false));
          return;
        }
        if (isRecurring) {
          await handleSetup();
        } else {
          await handlePayment();
        }
      } catch (error) {
        console.log("handleSubmit error =====", error);
      } finally {
        dispatch(onSetFinalDisclosureLoading(false));
        dispatch(setIsPaymentProcessing(false));
      }
    };

    useImperativeHandle(ref, () => ({
      callHandleClick: () => handleSubmit(),
    }));
    useEffect(() => {
      if (userData?.data?.accountData?.email) {
        setEmail(userData?.data?.accountData?.email || "");
      }
    }, [userData]);

    const handlePhoneCountryChange = useCallback((itm: string) => {
      setPhoneNumber((state) => ({
        ...state,
        country_code: getCountryCallingCode(itm as CountryCode),
      }));
    }, []);

    const handleOnPhoneChange = useCallback((phone: string) => {
      // if (phone?.length > 3) {
      // const isCheckValid = isValidPhoneNumber(phone);
      //   setIsValidNumber(isCheckValid);
      // } else {
      //   setIsValidNumber(true);
      // }
      setPhoneNumber((state) => ({
        ...state,
        phone,
      }));
    }, []);

    const amountCheck = useMemo(() => {
      if (isDowngradePlan || isUpdateSubscriptionPaymentMethod) {
        return false;
      }
      return parseFloat(`${amount}`) < 1;
    }, [amount, isDowngradePlan, isUpdateSubscriptionPaymentMethod]);

    if (isRetry && hasFuture) {
      return (
        <>
          <div className="none hidden">
            <PaymentElement />
          </div>
          <Button
            variant="variable"
            text={buttonText}
            className="w-full"
            isLoading={isSecretLoading || isLoading}
            isDisabled={isDisabled}
            onClick={() => {
              handleSubmit();
            }}
          />
        </>
      );
    }

    if (isRetry && !hasFuture) {
      return (
        <>
          <div className="none hidden">
            <PaymentElement />
          </div>
          <Button
            variant="variable"
            text={buttonText}
            className="max-w-40 w-full"
            isLoading={isSecretLoading || isLoading}
            isDisabled={isDisabled}
            onClick={() => {
              handleSubmit();
            }}
          />
        </>
      );
    }

    return (
      <>
        <div className="none hidden">
          <PaymentElement />
        </div>

        {isProductLink && (
          <div className="py-4">
            <div className="text-xs leading-[15.62px] font-bold text-txtSecondary mb-2">
              EMAIL
            </div>
            <div className="relative w-full">
              <input
                autoComplete="off"
                className={`w-full border-strokePrimary hover:!border-strokeActive rounded-[5px] border py-2 px-[15px] bg-bgPrimary text-sm text-txtPrimary font-normal leading-normal placeholder:text-txtSecondary focus:outline-none ${
                  userData?.data?.accountData?.email &&
                  "!cursor-not-allowed	!bg-bgSecondary"
                }`}
                placeholder="Email address"
                onChange={(e) => {
                  setEmail(
                    userData?.data?.accountData?.email
                      ? userData?.data?.accountData?.email
                      : e.target.value
                  );
                }}
                onBlur={handleEmailBlur}
                value={
                  userData?.data?.accountData?.email
                    ? userData?.data?.accountData?.email
                    : email
                }
                disabled={userData?.data?.accountData?.email}
              />
            </div>

            {collectPhone && (
              <div className="pt-5">
                <div
                  className={`!text-xs !font-bold !leading-normal text-txtSecondary uppercase `}
                >
                  Phone
                </div>
                <MobileInputNew
                  inputValue={phoneNumber?.phone || ""}
                  isProductLinkInput={true}
                  userCountry={
                    getCountryShortName(phoneNumber?.country_code) || "US"
                  }
                  setCountry={handlePhoneCountryChange}
                  setInputValue={handleOnPhoneChange}
                />
              </div>
            )}
          </div>
        )}

        {!isRetry &&
          (isAgreement ? (
            <div className="w-full flex lg:absolute bg-white pt-4 xl:pr-[3rem] lg:pl-[5rem] xl:pl-[7.5rem] lg:px-5 lg:-bottom-4 -bottom-48 left-0 justify-between items-center">
              <Button
                text={`Back`}
                variant="white"
                onClick={handleBack}
                className="lg:w-40 w-full max-w-40"
              />
              <Button
                text={`Confirm`}
                isDisabled={amountCheck || isEdit}
                isLoading={isLoading || isSecretLoading}
                onClick={() => {
                  handleSubmit();
                }}
                className="lg:w-40 w-full max-w-40"
                variant="variable"
              />
            </div>
          ) : isSchedulePayment ? (
            <div
              className="lg:absolute bg-white lg:bottom-0 left-0 py-4 z-20 border-t w-full gap-5"
            >
              <div
                className={`w-full px-5 ${isUpdatePlan ? "lg:px-10 flex gap-7" : "lg:px-8"}`}
              >
                {isUpdatePlan && (
                  <div className="lg:w-[5.2%] md2:w-[5.5%] w-0"></div>
                )}

                <div className="w-full flex justify-between gap-5">
                  <Button
                    className="lg:!w-40 !w-full"
                    isDisabled={isLoading}
                    text="Back"
                    type="button"
                    variant="secondary"
                    onClick={handleBack}
                  />
                  <Button
                    className="lg:!w-40 !w-full"
                    isLoading={isLoading || isSecretLoading}
                    isDisabled={amountCheck || isEdit}
                    text={isUpdatePlan ? "Update" : "Confirm"}
                    type="button"
                    onClick={() => {
                      handleSubmit();
                    }}
                    variant="variable"
                  />
                </div>
              </div>
            </div>
          ) : (
            <Button
              text={
                isUpdatePlan || isUpdateSubscription
                  ? "Update"
                  : `Pay ${
                      formatCurrency(parseFloat(`${amount || ""}`) + conFee) ||
                      0.0
                    }`
              }
              className={"!w-full mt-5"}
              isDisabled={
                amountCheck ||
                isEdit ||
                (isProductLink &&
                  (!validateEmail(email) || !isSubscriptionAllowed)) ||
                (collectPhone && !checkPhone(phoneNumber)) ||
                isFeeLoading
              }
              onClick={() => {
                handleSubmit();
              }}
              isLoading={isLoading || isSecretLoading || isChecking}
              variant={"variable"}
            />
          ))}
      </>
    );
  }
);

export default AmazonPayForm;
